import styled, { css } from "styled-components";
import type { ButtonVariant, ButtonSize } from "./types";

const variantStyles: Record<ButtonVariant, ReturnType<typeof css>> = {
  primary: css`
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primaryFg};
    box-shadow: ${({ theme }) => theme.shadows.primary};
    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.primaryHover};
    }
  `,
  secondary: css`
    background: ${({ theme }) => theme.colors.secondaryBg};
    color: ${({ theme }) => theme.colors.primary};
    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.secondaryBgHover};
    }
  `,
  outline: css`
    background: transparent;
    border: 1.5px solid ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.tintOrange50};
    }
  `,
  ghost: css`
    background: transparent;
    color: ${({ theme }) => theme.colors.fg2};
    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.gray50};
    }
  `,
  destructive: css`
    background: ${({ theme }) => theme.colors.danger};
    color: ${({ theme }) => theme.colors.senneWhite};
    &:hover:not(:disabled) {
      filter: brightness(0.92);
    }
  `,
  link: css`
    background: transparent;
    color: ${({ theme }) => theme.colors.primary};
    padding: 0 !important;
    &:hover:not(:disabled) { text-decoration: underline; }
  `,
};

const sizeStyles: Record<ButtonSize, ReturnType<typeof css>> = {
  sm: css`
    padding: 10px 18px;
    font-size: 12px;
    border-radius: ${({ theme }) => theme.radii.sm};
  `,
  md: css`
    padding: 14px 28px;
    font-size: 14px;
    border-radius: ${({ theme }) => theme.radii.md};
  `,
  lg: css`
    padding: 16px 32px;
    font-size: 16px;
    border-radius: ${({ theme }) => theme.radii.md};
  `,
};

export const Root = styled.button<{ $variant: ButtonVariant; $size: ButtonSize }>`
  font-family: ${({ theme }) => theme.typography.fontBody};
  font-weight: ${({ theme }) => theme.typography.fw.bold};
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all ${({ theme }) => theme.motion.durationBase} ${({ theme }) => theme.motion.easeStandard};

  &:active:not(:disabled) {
    transform: scale(0.98);
  }
  &:disabled {
    background: ${({ theme }) => theme.colors.gray100};
    color: ${({ theme }) => theme.colors.fg4};
    box-shadow: none;
    border: none;
    cursor: not-allowed;
  }

  ${({ $size }) => sizeStyles[$size]}
  ${({ $variant }) => variantStyles[$variant]}
`;
